import { FC, ReactNode } from "react";
import { Link } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { useTranslation } from "react-i18next";
import { logoPng, valleyDesktop, valleyPhone } from "../bridges/publicAssets";
import { usePublicPage } from "./usePublicPage";
import { PublicFooter } from "./PublicFooter";
import { RouteArt, SampleCards } from "./RouteArt";
import { PublicIcon, PublicIconName } from "./PublicIcon";
import { StatusArt, StatusKind } from "./StatusArt";
import { keyWords } from "./keyWords";
import { ContactButton } from "./PublicContact";
import { SITE_TABS } from "./navItems";
import s from "./public.module.scss";

export type PublicFrameProps = {
  /** Small label above the title (today's "WELCOME BACK"). */
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  /** The back arrow's address. Omit for the true entry screens. */
  backTo?: string; backLabel?: string;
  /** Today's small line under the tagline on the panel (desktop only). */
  footnote?: ReactNode;
  /** Under the content, e.g. "Don't have an account? Create account". */
  footer?: ReactNode;
  /** Status pages: the content sits in the middle of the sheet. */
  center?: boolean;
  /** Welcome: heading centred, roomier. */
  entry?: boolean;
  /** One functional icon above the kicker (board 300 / v5): key = forgot password, envelope-check = link sent, ... */
  icon?: PublicIconName; iconTone?: "warn" | "error";
  /** Status screens: the route-line illustration (v5 item 6) in place of the small icon. */
  statusArt?: StatusKind;
  /** The page's pictures (Anash/Shliach art of today) are not used in the new look; accepted so old callers compile. */
  art?: string; brandArt?: string; help?: boolean;
  /** Keep the footer (Terms, Contact us, language) off, e.g. while a registration step shows its own bar. */
  bare?: boolean;
  /** Registration: the sheet is handed over to the step (its own header, progress and action bar). */
  children2?: never;
};

/** The panel of board 298: valley picture under a light veil, the route line, the tagline and today's small line. Desktop only; a phone gets the band. */
export const PublicPanel = ({ footnote }: { footnote?: ReactNode }) => {
  const { t } = useTranslation();
  return (
    <aside className={s.panel} aria-hidden="true">
      <img className={s.panelBg} src={valleyDesktop} alt="" />
      <span className={s.veil} />
      <RouteArt className={s.route} />
      <SampleCards className={s.cards} />
      <div className={s.panelHead}>
        <div className={s.brandRow}><img className={s.logo} src={logoPng} alt="" /></div>
        <p className={s.tagline}>{keyWords(t("onboarding.tagline"), true)}</p>
        {footnote && <p className={s.dek}>{footnote}</p>}
      </div>
    </aside>
  );
};

/** The phone's top band: the valley picture under a veil with the logo and the tagline; the sheet overlaps its lower edge. */
export const PublicBand = ({ compact }: { compact?: boolean }) => {
  const { t } = useTranslation();
  return (
    <div className={`${s.band} ${compact ? s.bandCompact : ""}`} aria-hidden="true">
      <img className={s.panelBg} src={valleyPhone} alt="" />
      <span className={s.veil} />
      <div className={s.bandHead}>
        <div className={s.brandRow}><img className={s.logo} src={logoPng} alt="" /></div>
        {!compact && <p className={s.bandTagline}>{keyWords(t("onboarding.tagline"), true)}</p>}
      </div>
    </div>
  );
};

/** Desktop: today's home page behind the card, muted and lighter (v5 item 1). Pure decoration: no links, hidden from readers. */
export const MutedHome = () => {
  const { t } = useTranslation();
  return (
    <div className={s.muted} aria-hidden="true">
      <img className={s.mutedBg} src={valleyDesktop} alt="" />
      <span className={s.mutedVeil} />
      <div className={s.mutedHeader}>
        <img className={s.mutedLogo} src={logoPng} alt="" />
        <span className={s.mutedTabs}>{SITE_TABS.map((x) => <span key={x.key}>{t(`landing_nav.${x.key}`)}</span>)}</span>
      </div>
      <p className={s.mutedHeadline}>{t("first_page.hero_headline")}</p>
    </div>
  );
};

/** The new frame of Welcome, sign-in, forgot / reset and the status pages (boards 340-358): a centred card over the muted home page on a computer; band + sheet on a phone. */
export const PublicFrame: FC<PublicFrameProps> = ({ eyebrow, title, subtitle, children, backTo, backLabel, footnote, footer, center, entry, bare, icon, iconTone, statusArt }) => {
  const { t } = useTranslation();
  usePublicPage();
  return (
    <div className={`cy-next cy-public ${s.stage}`}>
      <MutedHome />
      <div className={s.card}>
        <PublicPanel footnote={footnote} />
        <PublicBand />
        <main className={`${s.sheet} ${center ? s.center : ""} ${entry ? s.entry : ""}`}>
          <div className={s.topRow}>
            {backTo ? (
              <Link to={backTo} className={s.back} aria-label={backLabel ? undefined : t("onboarding.go_back")}>
                <ArrowBackRoundedIcon aria-hidden fontSize="small" />{backLabel && <span>{backLabel}</span>}
              </Link>
            ) : <span />}
            <ContactButton />
          </div>
          <div className={s.content}>
            <div className={s.head}>
              {statusArt ? <StatusArt kind={statusArt} /> : icon && <span className={s.iconTile} data-tone={iconTone}><PublicIcon name={icon} size={28} /></span>}
              {eyebrow && <p className={s.eyebrow}>{eyebrow}</p>}
              <h1 className={s.title}>{keyWords(title)}</h1>
              {subtitle && <p className={s.sub}>{subtitle}</p>}
            </div>
            <div className={s.body}>{children}</div>
            {footer && <div className={s.foot}>{footer}</div>}
          </div>
          {!bare && <PublicFooter withLanguage contact={false} />}
        </main>
      </div>
    </div>
  );
};
